import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  Loader,
  type NestedListRowData,
  NestedListRowGroup,
  SkeletonLoader,
  TabContent,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodMessage } from '../../../../../../../../shared/components';
import {
  ENTITLEMENT_DETAILS_TABS,
  SEARCH_TEXT_LENGTH,
} from '../../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../../../shared/hooks';
import { detailsActions } from '../../../../../../../../shared/store/actions';
import {
  type RiskConflictsDisplayProps,
  type TrackedViolationFunctions,
} from '../../../../../../data-layer/detailsTypes';
import { closeAllNesting } from '../../../../../../utils';
import RiskConflictsHeader from '../ConflictsHeader/ConflictsHeader';
import {
  ConflictCenterContent,
  ConflictLeadingContent,
  ConflictTrailingContent,
} from '../ConflictsShared/ConflictsShared';

import './ConflictsDisplay.css';

/**
 *
 * * @fileoverview RiskConflictsDisplay - Displays risk conflicts in a nested list format
 * This component renders entitlement risk conflicts organized by function names,
 * with expandable rows showing entitlements and their associated objects.
 * Supports search, filtering, and side panel interactions.
 */
function RiskConflictsDisplay({
  activeTab,
  data = undefined,
  functionGroupedData = {},
  className = '',
  isLoading = false,
  hasMore = false,
  onLoadMore,
  functionCount = null,
}: RiskConflictsDisplayProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const [rowsData, setRowsData] = useState<NestedListRowData[]>([]);
  const [localLoadingMore, setLocalLoadingMore] = useState(false);

  const {
    entitlementConflicts: {
      selectedRowData,
      selectedRisk: conflictsRisk,
      sidepanel: { isSidePanelVisible },
      controls: { activeFilters = [], searchKey = null, expandKey = null } = {},
    },
    entitlementRisks: { selectedEntitlementRisk },
    entitlementSummary: { data: selectedEntitlement },
  } = useTypedSelector((state) => state.aag.details);

  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  const endpointNames = useMemo(() => [], []);

  const handleConflictRow = useCallback(
    (row: TrackedViolationFunctions) => {
      dispatch(detailsActions.setEntitlementConflictsSelectedRow(row));
      dispatch(detailsActions.setEntitlementConflictsSidePanelVisible(true));
    },
    [dispatch]
  );

  const processedData = useMemo(() => {
    // Get risk data to extract function information
    const riskData = conflictsRisk ?? selectedEntitlementRisk;
    const functions = [];

    // Extract functions from risk data dynamically
    if (riskData) {
      Object.keys(riskData).forEach((key) => {
        if (key.startsWith('function') && key.endsWith('Key')) {
          const functionNumber = key.replace('function', '').replace('Key', '');
          const functionNameKey = `function${functionNumber}`;

          const riskDataTyped = riskData as unknown as Record<string, unknown>;

          if (riskDataTyped[key] && riskDataTyped[functionNameKey]) {
            const functionKey = String(riskDataTyped[key]);

            functions.push({
              functionKey,
              functionName: String(riskDataTyped[functionNameKey]),
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              items: functionGroupedData[functionKey]?.data || [],
            });
          }
        }
      });
    }

    // If no functions found in risk data, create a default function
    if (functions.length === 0 && data && data.length > 0) {
      functions.push({
        functionKey: 'unknown',
        functionName: 'Conflicting Functions',
        items: data,
      });
    }

    return functions;
  }, [data, conflictsRisk, selectedEntitlementRisk, functionGroupedData]);

  // Create nested loader component
  const createNestedLoader = useCallback(
    (index: number): NestedListRowData => ({
      id: `${index}-loader`,
      isCheckboxDisabled: true,
      isDisabled: true,
      centerContent: (
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflictsLoader-centerContent'>
          <SkeletonLoader
            shape='square'
            animationStyle='wave'
            height='1rem'
            width='75%'
          />
          <SkeletonLoader
            shape='square'
            animationStyle='wave'
            height='2rem'
            width='100%'
          />
        </Box>
      ),
    }),
    []
  );

  useEffect(() => {
    setRowsData((prev) => {
      if (!processedData.length) {
        return prev.length > 0 ? prev : [];
      }

      return processedData.map(({ functionKey, functionName, items }) => {
        const functionId = Number(functionKey === 'unknown' ? 0 : functionKey);
        const existingRow = prev.find((row) => row.id === functionId);

        // Create function row data
        const fnRowData = {
          id: functionId,
          name: functionName,
          description: '',
          accountName: '',
          endpointName: selectedEntitlement?.endpointName ?? '',
          violatingEntitlementsCount:
            functionCount?.[functionKey]?.count ?? items.length,
          endpointLogo: selectedEntitlement?.endPointLogo ?? null,
        };

        return {
          id: functionId,
          data: {
            ...fnRowData,
            tracker: {
              id: functionId,
              level: 0,
              childrenCount: items.length,
              type: 'Function',
            },
          },
          isCheckboxDisabled: true,
          leadingComp: <ConflictLeadingContent level={0} />,
          centerContent: <ConflictCenterContent level={0} fnRow={fnRowData} />,
          trailingContent: (
            <ConflictTrailingContent level={0} fnRow={fnRowData} />
          ),
          connectorDistance: '1.625rem',
          afterContent:
            hasMore && onLoadMore ? (
              <Box className='Sod-RiskConflictsDisplay-afterContent'>
                <Button
                  kind='subtle'
                  size='medium'
                  onClick={async () => {
                    setLocalLoadingMore(true);

                    try {
                      await onLoadMore();
                    } catch (error) {
                      Logger.log('conflicts tab error', error);
                    }

                    setLocalLoadingMore(false);
                  }}
                  isDisabled={localLoadingMore}>
                  {l('Show More')}
                </Button>
              </Box>
            ) : undefined,
          childRows: [
            ...items.map((item, itemIndex) => {
              const typedItem = item as {
                object?: unknown[];
                showObjectFieldLabel?: boolean;
              };
              const isEntitlementObject = Boolean(
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                typedItem.showObjectFieldLabel && typedItem.object === null
              );
              const hasObjects =
                !isEntitlementObject &&
                Array.isArray(typedItem.object) &&
                typedItem.object.length > 0;
              const childId = `${functionId}-child-${itemIndex}`;
              const existingChildRow = existingRow?.childRows?.find(
                (child) => child.id === childId
              );

              return {
                id: childId,
                data: {
                  ...(item as unknown as Record<string, unknown>),
                  tracker: {
                    id: childId,
                    level: 1,
                    childrenCount: hasObjects ? typedItem.object?.length : 0,
                    type: isEntitlementObject ? 'Object' : 'Entitlement',
                  },
                },
                isCheckboxDisabled: true,
                isDisabled: isEntitlementObject,
                isExpandable: hasObjects,
                isNestedRowExpanded:
                  existingChildRow?.isNestedRowExpanded ?? false,
                leadingComp: (
                  <ConflictLeadingContent
                    level={1}
                    isObject={isEntitlementObject}
                  />
                ),
                centerContent: (
                  <ConflictCenterContent
                    level={1}
                    entRow={item as unknown as Record<string, unknown>}
                    vioRow={item as unknown as Record<string, unknown>}
                    isObject={isEntitlementObject}
                  />
                ),
                trailingContent: (
                  <ConflictTrailingContent
                    level={1}
                    vioRow={item as unknown as Record<string, unknown>}
                    isObject={isEntitlementObject}
                  />
                ),
                handleRowClick: () => {
                  handleConflictRow(
                    item as unknown as TrackedViolationFunctions
                  );
                },
                className:
                  String(selectedRowData?.tracker.id) === childId &&
                  isSidePanelVisible &&
                  selectedRowData?.tracker.level === 1
                    ? 'Sod-RiskConflictsDisplay-selectedViolatingRow'
                    : undefined,
                connectorDistance: '1.625rem',
                childRows: hasObjects
                  ? typedItem.object?.map((obj, objIndex) => {
                      const objectId = `${childId}-object-${objIndex}`;

                      return {
                        id: objectId,
                        data: {
                          ...(obj as Record<string, unknown>),
                          ...(item as unknown as Record<string, unknown>),
                          tracker: {
                            id: objectId,
                            level: 2,
                            childrenCount: 0,
                            type: 'Object',
                          },
                        },
                        isCheckboxDisabled: true,
                        isDisabled: true,
                        leadingComp: (
                          <ConflictLeadingContent level={2} isObject />
                        ),
                        centerContent: (
                          <ConflictCenterContent
                            level={2}
                            vioObj={obj as Record<string, unknown>}
                            isObject
                          />
                        ),
                        trailingContent: (
                          <ConflictTrailingContent level={2} isObject />
                        ),
                        connectorDistance: '1.625rem',
                      };
                    })
                  : [],
              };
            }),
            ...(localLoadingMore ? [createNestedLoader(functionId)] : []),
          ],
          isExpandable: items.length > 0,
          isNestedRowExpanded: existingRow?.isNestedRowExpanded ?? true,
        };
      });
    });
  }, [
    processedData,
    localLoadingMore,
    conflictsRisk,
    selectedEntitlementRisk,
    selectedEntitlement,
    hasMore,
    onLoadMore,
    createNestedLoader,
    selectedRowData,
    isSidePanelVisible,
    handleConflictRow,
    l,
    functionCount,
  ]);

  // Clear selected row when sidepanel closes (only if not switching tabs)
  useEffect(() => {
    if (
      !isSidePanelVisible &&
      selectedRowData &&
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS
    ) {
      dispatch(detailsActions.setEntitlementConflictsSelectedRow(null));
    }
  }, [dispatch, isSidePanelVisible, selectedRowData, activeTab]);

  // Handle expand/collapse functionality
  useEffect(() => {
    if (expandKey !== null) {
      if (expandKey === '-1') {
        // Collapse all
        setRowsData((prevRowsData) => closeAllNesting(prevRowsData));
        dispatch(detailsActions.setEntitlementConflictsExpandKey(null));
      } else if (expandKey === '1') {
        // Expand functions only (level 0)
        setRowsData((prevRowsData) =>
          prevRowsData.map((row) => ({
            ...row,
            isNestedRowExpanded: true,
            childRows: row.childRows?.map((child) => ({
              ...child,
              isNestedRowExpanded: false,
            })),
          }))
        );
        dispatch(detailsActions.setEntitlementConflictsExpandKey(null));
      } else if (expandKey === '2') {
        // Expand all levels
        setRowsData((prevRowsData) =>
          prevRowsData.map((row) => ({
            ...row,
            isNestedRowExpanded: true,
            childRows: row.childRows?.map((child) => ({
              ...child,
              isNestedRowExpanded: true,
            })),
          }))
        );
        dispatch(detailsActions.setEntitlementConflictsExpandKey(null));
      }
    }
  }, [dispatch, expandKey]);

  const renderBody = useCallback(() => {
    if (isLoading && !localLoadingMore) {
      return (
        <Box className='Sod-Violations-loaderWrapper Sod-RiskConflictsDisplay--isLoading'>
          <Loader kind='dots' format='inline' color='brand' />
        </Box>
      );
    }

    const hasAnyData = processedData.some((func) => func.items.length > 0);

    if (!hasAnyData) {
      return (
        <SodMessage
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          handleFilterClearBtn={() => {
            dispatch(detailsActions.setEntitlementConflictsActiveFilters([]));
          }}
          defaultTitle={l('No Conflicting Job Functions')}
          defaultMessage={l('No Conflicting Job Functions found')}
        />
      );
    }

    const scrollWrapperClasses = cx(
      'Sod-RiskConflictsTabDetails-nestedListGroupScrollArea',
      isSidePanelVisible &&
        'Sod-RiskConflictsTabDetails-nestedListGroupScrollArea--isSidePanelVisible',
      selectedRowData &&
        isSidePanelVisible &&
        'Sod-RiskConflictsDisplay-hasSelectedRow'
    );

    return (
      <Box
        tag='div'
        className={scrollWrapperClasses}
        data-selected-row-id={selectedRowData?.id ?? ''}>
        <NestedListRowGroup
          key={`conflicts-${processedData.length}`}
          rowsData={rowsData}
          setRowsData={setRowsData}
          selectedRowData={
            selectedRowData
              ? rowsData.find(
                  (row) =>
                    row.id === selectedRowData.id ||
                    row.childRows?.find(
                      (child) => child.id === selectedRowData.id
                    )
                ) ??
                rowsData
                  .flatMap((row) => row.childRows ?? [])
                  .find((child) => child.id === selectedRowData.id)
              : null
          }
          setSelectedRowData={(row) => {
            handleConflictRow(row as TrackedViolationFunctions);
          }}
          tallestRowHeight={5.5}
          afterContentHeight={hasMore && onLoadMore ? 4.5 : 2.5}
        />
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    processedData.length,
    data,
    isSidePanelVisible,
    rowsData,
    selectedRowData,
    isSearchActive,
    isFilterActive,
    l,
    dispatch,
    handleConflictRow,
    hasMore,
    onLoadMore,
  ]);

  const conflictTabContentClasses = cx(
    'Sod-RiskConflictsDisplay',
    isSidePanelVisible && 'Sod-RiskConflictsDisplay--isSidePanelVisible',
    className
  );

  return (
    <TabContent
      value={activeTab}
      tabValue={ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS}
      className={conflictTabContentClasses}>
      <RiskConflictsHeader
        endpointNames={endpointNames}
        isDisabled={isLoading}
      />
      {renderBody()}
    </TabContent>
  );
}

RiskConflictsDisplay.defaultProps = {
  hasMore: false,
  onLoadMore: () => {},
  functionGroupedData: {},
};
export default RiskConflictsDisplay;
